"use client";

import log from "logbeacon";
import { useMemoizedFn } from "ahooks";
import { useHandleImportClick } from "./useHandleImportClick";
import { useResponsiveChatOpen } from "./useResponsiveChatOpen";
import { openAddAccountModal } from "@/src/components/AddAccountModal";
import { openPortfolioAccountsModal } from "@/src/components/PlaidImportModal";
import { PORTFOLIO_UPLOAD_HOLDINGS_CHAT_QUESTION } from "@/src/components/PlaidImportModal/portfolioUploadHoldingsChatQuestion";
import type { PlaidBindSuccessContext } from "@/src/components/PlaidImportModal/types";
import { sleep } from "@utils/sleep";
import { OnboardingPaywallFreeTrialContentProps } from "@components/OnboardingWizard/Paywall";

export interface UseAccountConnectionFlowOptions {
  /** Runs after built-in folder refresh on Plaid bind success. */
  onBindSuccess?: (ctx: PlaidBindSuccessContext) => void | Promise<void>;
  /** Runs when user closes the Plaid import modal. */
  onComplete?: () => void;
  paywallCopyOverrides?: OnboardingPaywallFreeTrialContentProps["paywallCopyOverrides"];
  // 允许自动打开管理弹窗
  autoOpenManageModal?: boolean;
}

export interface UseAccountConnectionFlowReturn {
  /** Ghost state / connect hero entry — routes to manage modal or fresh connect. */
  startFlow: () => Promise<void>;
  /** Manage button entry — opens accounts modal, wires the AddAccount drawer. */
  startManageFlow: () => Promise<void>;
  /** Runs when user opens the Add Account drawer. */
  openAddAccount?: () => void | Promise<void>;
}

/**
 * Orchestrates the Add-Account drawer + Plaid connect + manage modal flow.
 *
 * Flow graph:
 *   startFlow → handleImportClick (manage if connections exist, else fresh connect)
 *   startManageFlow → openPortfolioAccountsModal
 *       └─ onAddAccount → openAddAccountModal
 *             ├─ onConnect → handleImportClick({ forceFreshConnect: true })
 *             └─ onUpload → openResponsiveChat(upload question)
 */
export const useAccountConnectionFlow = (
  options?: UseAccountConnectionFlowOptions,
): UseAccountConnectionFlowReturn => {
  const { onBindSuccess, onComplete, paywallCopyOverrides, autoOpenManageModal = true } = options || {};

  const { handleImportClick } = useHandleImportClick({
    onBindSuccess: async (ctx) => {
      await sleep(2000);
      onBindSuccess?.(ctx);
      autoOpenManageModal && startManageFlow?.();
    },
    onComplete,
    paywallCopyOverrides,
  });

  const { openResponsiveChat } = useResponsiveChatOpen();

  const handleUploadHoldings = useMemoizedFn(() => {
    openResponsiveChat({
      question: PORTFOLIO_UPLOAD_HOLDINGS_CHAT_QUESTION,
    });
  });

  const openAddAccount = useMemoizedFn(() => {
    void openAddAccountModal({
      onConnect: () => {
        void handleImportClick({ forceFreshConnect: true });
      },
      onUpload: handleUploadHoldings,
    });
  });

  const startManageFlow = useMemoizedFn(async () => {
    try {
      await openPortfolioAccountsModal({
        onBindSuccess,
        onAddAccount: openAddAccount,
      });
    } catch (error) {
      log.error("[useAccountConnectionFlow] manage flow error:", error);
    }
  });

  const startFlow = useMemoizedFn(async () => {
    await handleImportClick();
  });

  return {
    startFlow,
    startManageFlow,
    openAddAccount,
  };
};
